import { useEffect, useState } from "react";
import { TUTORIAL_STAGE } from "@reagent/content";
import { subscribeEvents, useGameStore } from "../../app/gameStore";
import { useRunStore } from "../../app/runStore";
import { markTutorialDone } from "../../app/tutorialState";
import { playCue } from "../../audio/AudioEngine";
import { TUTORIAL_STEPS } from "./steps";
import "./tutorial.css";

/**
 * The induction's guided overlay.
 *
 * It renders only on the induction board, so leaving the tutorial is a matter
 * of loading any other stage — there is no mode to get stuck in. It also never
 * blocks the board: it is a card in the corner, not a modal, because a tutorial
 * that greys out the thing it is describing is teaching you to look at the
 * tutorial.
 *
 * Skipping is offered on every step and is not a lesser path. A player who
 * already knows how to drag a card should not have to prove it.
 */
export function Tutorial() {
  const stageId = useGameStore((s) => s.stageId);
  const loadRunStage = useGameStore((s) => s.loadRunStage);
  const newRun = useRunStore((s) => s.newRun);

  const [index, setIndex] = useState(0);
  const [dismissed, setDismissed] = useState(false);

  const onInduction = stageId === TUTORIAL_STAGE.id;
  const step = TUTORIAL_STEPS[index];
  const active = onInduction && !dismissed && step !== undefined;

  /**
   * Advances exactly once.
   *
   * Both listeners below keep firing for as long as their condition holds —
   * a store subscription runs on every state change, and "the phase is run"
   * stays true for eight ticks. Without this latch a single satisfied step
   * advanced the tutorial two or three panels at once, skipping the ones in
   * between. The effect's own cleanup is too late: several notifications
   * arrive before React re-runs it.
   */
  const advanceOnce = () => {
    let fired = false;
    return () => {
      if (fired) return;
      fired = true;
      setIndex((i) => i + 1);
    };
  };

  // Snapshot-driven steps. Subscribing to the store rather than reading a
  // hook value keeps this out of the render path: a step advances when the
  // board says so, however the board got there.
  useEffect(() => {
    if (!active || !step?.whenSnapshot) return;
    const check = step.whenSnapshot;
    const advance = advanceOnce();
    if (check(useGameStore.getState().snapshot)) {
      advance();
      return;
    }
    return useGameStore.subscribe((state) => {
      if (check(state.snapshot)) advance();
    });
  }, [active, step]);

  // Event-driven steps, for the things a snapshot cannot tell you happened.
  useEffect(() => {
    if (!active || !step?.whenEvent) return;
    const matches = step.whenEvent;
    const advance = advanceOnce();
    return subscribeEvents((event) => {
      if (matches(event)) advance();
    });
  }, [active, step]);

  if (!active || !step) return null;

  const isLast = index === TUTORIAL_STEPS.length - 1;

  /** Ends the induction and starts a real shift on a clean run. */
  const clockOn = () => {
    playCue("switchThrow");
    markTutorialDone();
    // A fresh seed, so the first real shift is not the one that was sitting
    // behind the tutorial all along.
    newRun();
    loadRunStage();
  };

  const skip = () => {
    markTutorialDone();
    setDismissed(true);
  };

  return (
    <aside className="tutorial" aria-live="polite">
      <div className="tutorial__count">
        induction · {index + 1} of {TUTORIAL_STEPS.length}
      </div>
      <h2 className="tutorial__title">{step.title}</h2>
      <p className="tutorial__body">{step.body}</p>

      <div className="tutorial__actions">
        {step.button && (
          <button
            className="tutorial__go"
            onClick={() => {
              if (isLast) clockOn();
              else {
                playCue("uiClick");
                setIndex((i) => i + 1);
              }
            }}
          >
            {step.button}
          </button>
        )}
        {!step.button && <span className="tutorial__waiting">waiting for you…</span>}
        <button className="tutorial__skip" onClick={skip}>
          skip induction
        </button>
      </div>

      <ol className="tutorial__pips" aria-hidden>
        {TUTORIAL_STEPS.map((s, i) => (
          <li key={s.id} className={i < index ? "is-done" : i === index ? "is-current" : ""} />
        ))}
      </ol>
    </aside>
  );
}
